import { useLocation } from "react-router-dom";
import { z } from "zod";

import { API } from "@js/api";
import { fetchCurrentUser } from "@js/apps/auth/actions";
import { useEffectOnce } from "@js/apps/common/hooks/use-effect-once";
import { WebSocketManagerContainer } from "@js/components/websocket";
import { useAppDispatch, useNavigate } from "@js/hooks";
import { useEffectRef } from "@js/hooks/use-effect-ref";
import type { UserPermission } from "@js/types/auth";

const CAN_VIEW_INVOICES_LISTENER = "employer-permission-changed-listener";

const customPermissionValues = Object.values(ENUMS.CustomPermission);
const employerPermissionChangeSchema = z
  .object({
    broadcast_type: z.literal(ENUMS.BroadcastType.EMPLOYER_PERMISSION_CHANGED),
  })
  .extend(
    Object.fromEntries(
      customPermissionValues.map((key) => [key, z.boolean().optional()]),
    ) as Record<UserPermission, z.ZodOptional<z.ZodBoolean>>,
  );

const employerPermissionChangeEventSchema = z
  .object({
    data: employerPermissionChangeSchema,
  })
  .passthrough();

export const EmployerPermissionChangeProvider = () => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const pathnameRef = useEffectRef(pathname);

  useEffectOnce(() => {
    const listener = async (event: MessageEvent) => {
      const parsedEvent = employerPermissionChangeEventSchema.safeParse(event);
      if (!parsedEvent.success) {
        return;
      }

      dispatch(API.util.invalidateTags(["EmployerTeamMembers"]));
      await dispatch(fetchCurrentUser()).catch(() => null);
      // remount the page
      navigate(pathnameRef.current, { replace: true });
    };

    WebSocketManagerContainer.attachListener(
      listener,
      CAN_VIEW_INVOICES_LISTENER,
    );

    return () => {
      WebSocketManagerContainer.removeListener(CAN_VIEW_INVOICES_LISTENER);
    };
  });

  return <></>;
};
